import { action, makeObservable, observable } from "mobx";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { CombinatorRuleType } from "models/RuleCombinator";
import { formatSegmentHeading } from "components/UI/AcxTranscription/utils";

export interface IOutlineFilter {
    value: boolean; // true is "contains", false is "does not contain"
    segments: string[];
    filterGroupId: number;
}

export interface OutlineSegmentOption {
    id: string;
    name: string;
}

// This is a static list and needs to match the list on the backend
// AcxPlatform2/AcxPlatform/Controllers/Common/ApplicationFilters/Models/OutlineSegmentKeys.cs
const outlineSegmentKeys = [
    "GREETING",
    "VERIFICATION",
    "CALL_REASON/PROBLEM_STATEMENT",
    "SOLUTION_PROVIDED",
    "CLOSING",
    "IVR",
    "VOICEMAIL",
    "CLARIFICATION/GATHERING_INFO",
    "TRANSFER/HOLD",
] as const;

export const allOutlineSegmentOptions: OutlineSegmentOption[] = outlineSegmentKeys.map(
    (segmentKey) => ({
        id: segmentKey,
        name: formatSegmentHeading(segmentKey),
    }),
);

export const initOutlineFilterItems: IOutlineFilter[] = [
    {
        value: true,
        segments: [],
        filterGroupId: 0,
    },
];

export default class OutlineFilterStore extends BaseStore {
    @observable
    outlineFilterItems: IOutlineFilter[] = initOutlineFilterItems;

    readonly outlineSegmentOptions = allOutlineSegmentOptions;

    constructor(public rootStore: IRootStore) {
        super("OutlineFilterStore");
        makeObservable(this);
    }

    @action
    setOutlineFilterItem = (index: number, outlineFilter: IOutlineFilter) => {
        this.outlineFilterItems[index] = outlineFilter;
    };

    @action
    setOutlineFilterItems = (outlineFilters: IOutlineFilter[]) => {
        this.outlineFilterItems = outlineFilters;
    };

    @action
    updateOutlineItemContains = (index: number, value: boolean) => {
        this.outlineFilterItems[index].value = value;
    };

    @action
    addOutlineFilterItem = (combinator: CombinatorRuleType) => {
        const newItem: IOutlineFilter = {
            value: true,
            segments: [],
            filterGroupId: 0,
        };

        if (this.outlineFilterItems.length) {
            const lastItemFilterGroupId =
                this.outlineFilterItems[this.outlineFilterItems.length - 1]
                    ?.filterGroupId;

            newItem.filterGroupId =
                combinator === CombinatorRuleType.And
                    ? lastItemFilterGroupId
                    : lastItemFilterGroupId + 1;
        }

        this.outlineFilterItems.push(newItem);
    };

    @action
    removeOutlineFilterItem = (inputIndex: number) => {
        if (this.outlineFilterItems.length === 1) return;
        this.outlineFilterItems = this.outlineFilterItems.filter(
            (_, index) => index !== inputIndex,
        );
    };

    @action
    changeOutlineCombinator = (index: number, input: CombinatorRuleType) => {
        const thisItem = this.outlineFilterItems[index];
        const nextItem = this.outlineFilterItems[index + 1];
        const itemCount = this.outlineFilterItems.length;
        const newFilterId =
            input === CombinatorRuleType.Or
                ? thisItem.filterGroupId + 1
                : thisItem.filterGroupId;

        nextItem.filterGroupId = newFilterId;

        for (let i = index + 2; i < itemCount; i++) {
            const currentItem = this.outlineFilterItems[i];
            if (input === CombinatorRuleType.And) {
                currentItem.filterGroupId--;
            } else {
                currentItem.filterGroupId++;
            }
        }
    };

    /**
     * Removes the first found outline segment from the filter list by id.
     * @param id
     * @returns true if the segment was found and removed, false otherwise.
     */
    @action
    removeSegmentById = (id: string) => {
        for (let i = 0; i < this.outlineFilterItems.length; i++) {
            const item = this.outlineFilterItems[i];
            if (item.segments.includes(id)) {
                const newSegments = item.segments.filter(
                    (segmentId) => segmentId !== id,
                );

                if (newSegments.length === 0) {
                    if (this.outlineFilterItems.length === 1) this.setOutlineFilterItems(initOutlineFilterItems);
                    else this.removeOutlineFilterItem(i);
                } else {
                    this.setOutlineFilterItem(i, {
                        ...item,
                        segments: newSegments,
                    });
                }

                return true;
            }
        }

        return false;
    };
}
